const extractMigrationWrappedMessage = (message) => {
  const m = String(message || '').trim();
  if (!m) return null;
  const match = m.match(/^UserMigration failed with error\s+(.*)$/i);
  if (!match) return null;
  const inner = String(match[1] || '').trim();
  return inner || null;
};

const safeString = (value) => {
  if (value === null || value === undefined) return '';
  return String(value);
};

const NO_VERIFIED_DELIVERY_FOR_PASSWORD_RESET_PATTERN =
  /cannot\s+reset\s+password.*no\s+registered\/verified\s+email\s+or\s+phone_number/i;

export const getCognitoErrorCode = (err) => {
  return safeString(err?.code || err?.name || err?.__type).trim();
};

export const getCognitoErrorMessage = (err) => {
  return safeString(err?.message).trim();
};

export const getUnwrappedMigrationMessage = (err) => {
  const message = getCognitoErrorMessage(err);
  return extractMigrationWrappedMessage(message);
};

export const isCognitoUserNotFoundError = (err) => {
  const code = getCognitoErrorCode(err);
  const message = getCognitoErrorMessage(err);
  const unwrapped = getUnwrappedMigrationMessage(err) || '';

  if (code === 'UserNotFoundException') return true;
  if (/user not found|user does not exist/i.test(message)) return true;
  if (/user not found|user does not exist/i.test(unwrapped)) return true;

  return false;
};

export const isCognitoPasswordResetRequiredError = (err) => {
  const code = getCognitoErrorCode(err);
  const message = getCognitoErrorMessage(err);
  const unwrapped = getUnwrappedMigrationMessage(err) || '';

  if (code === 'PasswordResetRequiredException') return true;
  if (/password reset required/i.test(message)) return true;
  if (/password reset required/i.test(unwrapped)) return true;

  return false;
};

export const isCognitoUserDisabledError = (err) => {
  const code = getCognitoErrorCode(err);
  const message = getCognitoErrorMessage(err);
  const unwrapped = getUnwrappedMigrationMessage(err) || '';

  // Cognito often surfaces disabled users as NotAuthorizedException with message "User is disabled."
  if (code === 'NotAuthorizedException' && /user is disabled/i.test(message)) return true;
  if (code === 'NotAuthorizedException' && /user is disabled/i.test(unwrapped)) return true;
  if (/user is disabled/i.test(message)) return true;

  return false;
};

export const isCognitoPasswordCannotBeResetInCurrentStateError = (err) => {
  const code = getCognitoErrorCode(err);
  const message = getCognitoErrorMessage(err);
  const unwrapped = getUnwrappedMigrationMessage(err) || '';

  if (code !== 'NotAuthorizedException') return false;

  return (
    /password\s+cannot\s+be\s+reset\s+in\s+the\s+current\s+state/i.test(message) ||
    /password\s+cannot\s+be\s+reset\s+in\s+the\s+current\s+state/i.test(unwrapped)
  );
};

export const isCognitoUserNotConfirmedError = (err) => {
  const code = getCognitoErrorCode(err);
  const message = getCognitoErrorMessage(err);
  const unwrapped = getUnwrappedMigrationMessage(err) || '';

  if (code === 'UserNotConfirmedException') return true;
  if (/not\s+confirmed/i.test(message)) return true;
  if (/not\s+confirmed/i.test(unwrapped)) return true;

  return false;
};

// Cognito forgot-password sometimes fails with InvalidParameterException when it cannot
// deliver a reset code (no verified email/phone). We treat it as a "verify email first" case.
export const isCognitoNoVerifiedDeliveryForPasswordResetError = (err) => {
  const code = getCognitoErrorCode(err);
  if (code !== 'InvalidParameterException') return false;

  const message = getCognitoErrorMessage(err);
  const unwrapped = getUnwrappedMigrationMessage(err) || '';

  return (
    NO_VERIFIED_DELIVERY_FOR_PASSWORD_RESET_PATTERN.test(message) ||
    NO_VERIFIED_DELIVERY_FOR_PASSWORD_RESET_PATTERN.test(unwrapped)
  );
};

export const isCognitoUserAlreadyConfirmedError = (err) => {
  const code = getCognitoErrorCode(err);
  if (code !== 'InvalidParameterException') return false;

  const message = getCognitoErrorMessage(err);
  const unwrapped = getUnwrappedMigrationMessage(err) || '';

  return /user\s+is\s+already\s+confirmed/i.test(message) || /user\s+is\s+already\s+confirmed/i.test(unwrapped);
};

export const normalizeCognitoErrorMessage = (err, options = {}) => {
  const code = getCognitoErrorCode(err);
  const message = getCognitoErrorMessage(err);
  const unwrapped = getUnwrappedMigrationMessage(err);

  const defaultMessage = options.defaultMessage || 'Request failed';

  const isMigrationFailure =
    code === 'UserLambdaValidationException' ||
    code === 'InvalidLambdaResponseException' ||
    /user migration/i.test(message) ||
    (/migration/i.test(message) && /(failed|error)/i.test(message));

  if (isCognitoUserNotFoundError(err) || isMigrationFailure) {
    return unwrapped || message || 'User not found.';
  }

  return message || defaultMessage;
};

export const getCognitoChangePasswordErrorI18nKey = (err) => {
  const code = getCognitoErrorCode(err);
  const message = getCognitoErrorMessage(err);

  // Cognito uses NotAuthorizedException for wrong current/old password.
  if (code === 'NotAuthorizedException' || /incorrect username or password/i.test(message)) {
    return 'UI.changePasswordErrors.wrongOldPassword';
  }

  if (code === 'InvalidPasswordException') {
    return 'UI.changePasswordErrors.invalidNewPassword';
  }

  if (code === 'LimitExceededException' || code === 'TooManyRequestsException') {
    return 'UI.changePasswordErrors.tooManyAttempts';
  }

  if (code === 'PasswordHistoryPolicyViolationException' || /Password has previously been used/i.test(message)) {
    return 'Errors.PasswordHistoryPolicy';
  }

  return 'UI.changePasswordErrors.generic';
};

export const getCognitoResetPasswordErrorI18nKey = (err) => {
  const code = getCognitoErrorCode(err);
  const message = getCognitoErrorMessage(err);

  if (code === 'CodeMismatchException' || /invalid verification code/i.test(message)) {
    return 'Errors.invalidVerificationCode';
  }

  if (code === 'ExpiredCodeException' || /expired/i.test(message)) {
    return 'Errors.mfa.expiredCode';
  }

  if (code === 'LimitExceededException' || code === 'TooManyRequestsException') {
    return 'Errors.mfa.tooManyAttempts';
  }

  if (code === 'InvalidPasswordException') {
    return 'Errors.weakPassword';
  }

  if (code === 'PasswordHistoryPolicyViolationException' || /Password has previously been used/i.test(message)) {
    return 'Errors.PasswordHistoryPolicy';
  }

  return null;
};

export const getCognitoResetPasswordFieldError = (err) => {
  const code = getCognitoErrorCode(err);
  const message = getCognitoErrorMessage(err);

  if (code === 'CodeMismatchException' || /invalid verification code/i.test(message)) {
    return { fieldName: 'verificationCode', i18nKey: 'Errors.invalidVerificationCode' };
  }

  if (code === 'ExpiredCodeException' || /expired/i.test(message)) {
    return { fieldName: 'verificationCode', i18nKey: 'Errors.verificationCodeExpired' };
  }

  if (code === 'InvalidPasswordException') {
    return { fieldName: 'password', i18nKey: 'Errors.weakPassword' };
  }

  if (code === 'PasswordHistoryPolicyViolationException' || /Password has previously been used/i.test(message)) {
    return { fieldName: 'password', i18nKey: 'Errors.PasswordHistoryPolicy' };
  }

  return null;
};

export const getCognitoMfaErrorI18nKey = (err) => {
  const code = getCognitoErrorCode(err);
  const message = getCognitoErrorMessage(err);

  if (
    code === 'NotAuthorizedException' &&
    /too many invalid credentials attempts|temporarily locked|try again after/i.test(message)
  ) {
    return 'Errors.mfa.tooManyAttempts';
  }

  if (
    code === 'NotAuthorizedException' &&
    /invalid session|session is expired|session expired|expired session/i.test(message)
  ) {
    return 'Errors.mfa.expiredCode';
  }

  if (
    code === 'CodeMismatchException' ||
    /code mismatch|invalid verification code|invalid code|incorrect code/i.test(message)
  ) {
    return 'Errors.mfa.invalidCode';
  }

  if (code === 'ExpiredCodeException' || /expired/i.test(message)) {
    return 'Errors.mfa.expiredCode';
  }

  if (code === 'LimitExceededException' || code === 'TooManyRequestsException') {
    return 'Errors.mfa.tooManyAttempts';
  }

  return null;
};
